import { useEffect } from 'react';
import { useEventTracking } from '@services/statsig';
import { isTextEntryTarget } from '@utils/keyboard';

import { MODAL_TYPES } from '../components/modals/constants';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectModals } from '../store/selectors';
import { popModal, pushModal } from '../store/slices/modalSlice';

/**
 * App-wide keyboard shortcuts, mounted once in AuthenticatedLayout
 * (listener pattern: Nav.tsx Cmd+P). ⌘K/Ctrl+K toggles the command palette:
 * opens when nothing modal is on top and the user isn't typing; closes when
 * the palette itself is the top modal (even from its own input).
 */
export const useGlobalKeyboardShortcuts = (): void => {
  const dispatch = useAppDispatch();
  const modals = useAppSelector(selectModals);
  const { commandPalette } = useEventTracking();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      const topModal = modals[modals.length - 1];
      if (topModal?.modalType === MODAL_TYPES.COMMAND_PALETTE) {
        event.preventDefault();
        dispatch(popModal());
        return;
      }
      if (topModal || isTextEntryTarget(event.target)) return;
      event.preventDefault();
      const surface = window.location.pathname.split('/').filter(Boolean).pop() ?? 'root';
      commandPalette.opened({ surface, via: 'keyboard' });
      dispatch(
        pushModal({
          modalType: MODAL_TYPES.COMMAND_PALETTE,
          modalProps: {
            maxWidth: 'sm',
            sx: { alignSelf: 'flex-start', borderRadius: '10px', mt: '96px' },
          },
        }),
      );
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, modals, commandPalette]);
};
